import { useEffect, useState } from "react";
import { Button, Flex, Modal, Text, Textarea, TextInput } from "@mantine/core";
import styled from "styled-components";
import {
  removeWorkdayCorrection,
  upsertWorkdayCorrection,
} from "../../api/reportsApi";
import { errorNotification } from "../../utils/useNotification";

/**
 * EKSİK ÇIKIŞ DÜZELTMESİ — gün satırının yanındaki saat kutusu + not alanı
 * (plan 2.49-B).
 *
 * ⚠ NE YAPMIYOR: sahte bir çıkış kaydı ÜRETMİYOR. Ham defter
 * (`employeeActivities`) imzalı bir ölçüm ve dokunulmaz; buradan yazılan şey
 * ayrı bir katmanda duran bir BEYAN. Bu yüzden hücrede "17:00" değil
 * "17:00 · düzeltildi" yazıyor ve notu görünür — çalışan da mobilde aynısını
 * görecek. İK'nın görünmeden saat yazabildiği bir sistem, şeffaflık için
 * eklenen özelliğin tersini üretirdi.
 *
 * ⚠ SAAT YEREL: `datetime-local` girdisi tarayıcının yerel saatinde okunur ve
 * `new Date(...)` ile UTC'ye çevrilir. Doğrulamanın tamamı sunucuda —
 * "bu saat o iş gününe mi düşüyor", "son girişten sonra mı", "gelecek mi",
 * "gün gerçekten eksik çıkışlı mı" — çünkü hepsi kişinin o günkü kesim
 * saatini ve hareketlerini bilmeyi gerektiriyor. Buradaki tek kontrol, boş
 * gövde göndermemek.
 */

/** `Date` → `datetime-local` girdisinin beklediği yerel `YYYY-MM-DDTHH:mm`. */
const toLocalInput = (value?: string | null): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/**
 * Düzeltme kutusunun varsayılan saati: o iş gününün ÖĞLEDEN SONRASI değil,
 * boş. Bilerek — hazır bir saat sunmak İK'yı düşünmeden onaylamaya iter ve
 * beyanın değeri düşer.
 */
export type CorrectionTarget = {
  userId: string;
  userName: string;
  workday: string;
  corrected: boolean;
  correctedExit?: string | null;
  correctionNote?: string | null;
};

const MissingExitCorrection = ({
  target,
  onClose,
  onSaved,
}: {
  target: CorrectionTarget | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [exitAt, setExitAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setExitAt(toLocalInput(target?.correctedExit));
    setNote(target?.correctionNote ?? "");
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    if (!exitAt) {
      errorNotification({
        title: "Saat Gerekli.",
        message: "Çıkış saatini girin.",
      });
      return;
    }
    if (!note.trim()) {
      // Not zorunlu: "neden" olmadan bu kayıt ay sonunda denetlenemez.
      errorNotification({
        title: "Not Gerekli.",
        message: "Düzeltmenin gerekçesini yazın.",
      });
      return;
    }
    setSaving(true);
    try {
      await upsertWorkdayCorrection({
        employeeId: target.userId,
        workday: target.workday,
        exitAt: new Date(exitAt).toISOString(),
        note: note.trim(),
      });
      onSaved();
      onClose();
    } catch (error: any) {
      // Sunucunun mesajı OLDUĞU GİBİ gösteriliyor: dört doğrulama kuralının
      // hangisine takıldığını yalnızca o biliyor ("girilen saat 2026-09-07 iş
      // gününe düşüyor" gibi). Genel bir "hata oluştu" burada işe yaramaz.
      errorNotification({
        title: "Düzeltme Kaydedilemedi.",
        message:
          error?.response?.data?.message ??
          "Düzeltme kaydedilirken bir hata oluştu.",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await removeWorkdayCorrection({
        employeeId: target.userId,
        workday: target.workday,
      });
      onSaved();
      onClose();
    } catch (error: any) {
      errorNotification({
        title: "Düzeltme Kaldırılamadı.",
        message:
          error?.response?.data?.message ??
          "Düzeltme kaldırılırken bir hata oluştu.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened
      centered
      radius="20px"
      onClose={onClose}
      title={<ModalTitle>Çıkış Saati Düzeltmesi</ModalTitle>}
    >
      <Flex direction="column" gap="16px">
        <Text size="sm" c="dimmed">
          {target.userName} · {target.workday}
        </Text>

        {/*
          Beyanın ne OLMADIĞI burada yazıyor. Bu cümle olmadan İK, düzeltmeyi
          "çıkışı ben okuttum" diye anlıyor ve ham defterin anlamı bulanıyor.
        */}
        <Notice>
          Bu bir <b>beyandır</b>, okutma kaydı değil. Çalışanın giriş-çıkış
          defteri değişmez; not ve saat çalışana da görünür.
        </Notice>

        <TextInput
          label="Çıkış saati"
          type="datetime-local"
          value={exitAt}
          onChange={(event) => setExitAt(event.currentTarget.value)}
        />

        <Textarea
          label="Gerekçe"
          placeholder="Örn: vardiya sonrası depoda kaldı, çıkışı okutmayı unuttu"
          minRows={3}
          autosize
          withAsterisk
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />

        <Flex justify="space-between" gap="10px">
          {target.corrected ? (
            <Button
              variant="outline"
              color="red"
              onClick={remove}
              loading={saving}
            >
              Düzeltmeyi kaldır
            </Button>
          ) : (
            <span />
          )}
          <Flex gap="10px">
            <Button variant="default" onClick={onClose} disabled={saving}>
              Vazgeç
            </Button>
            <Button onClick={submit} loading={saving}>
              Kaydet
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default MissingExitCorrection;

const ModalTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
`;

const Notice = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 18px;
`;
